import { useModal, DateField } from '@jutro/components';
import { BreakpointTrackerContext } from '@jutro/layout';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { messages as commonMessages } from '@xengage/gw-platform-translations';
import { useDataRefresh, ViewModelForm, ViewModelServiceContext } from '@xengage/gw-portals-viewmodel-react';
import { SubmissionService, PolicyService } from 'gw-capability-gateway';
import { ENTGatewayDocumentService } from 'ent-capability-gateway-document';
import _ from 'lodash';
import React, { useCallback, useContext, useState, useEffect } from 'react';
import config from 'app-config';
import { TranslatorContext } from '@jutro/locale';
import TWIADocumentUtil from '../../util/TWIADocumentUtil';
import messages from './DocumentUploadComponent.messages.js';
import metadata from './DocumentUploadComponent.metadata.json5';
import styles from './DocumentUploadComponent.module.scss';
import CpCoverablesService from '../../../../capabilities/twia-capability-quoteandbind-cp/services/CpCoverablesService.js';

import { Button, InputField, DropdownSelectField } from '@jutro/legacy/components';

import { Link } from '@jutro/router';

function DocumentUploadComponent(props) {
    const breakpoint = useContext(BreakpointTrackerContext);
    const { showAlert } = useModal();
    const { jobNumber, policyNumber, productCode, uwCompany, setDocumentCount, offerNumber } = props;
    const { authHeader } = useAuthentication();
    const { refreshData } = useDataRefresh();
    const [documentSearch, setDocumentSearch] = useState('');
    const [documentTypes, setDocumentTypes] = useState([]);
    const [documentType, setDocumentType] = useState();
    const [documents, setDocuments] = useState([]);
    const [displayDocuments, setDisplayDocuments] = useState([]);
    const [buildings, setBuildings] = useState([]);
    const [loading, setLoading] = useState(false);
    const viewModelService = useContext(ViewModelServiceContext);
    const translator = useContext(TranslatorContext);
    const documentFilterType = TWIADocumentUtil.getPolicyDocumentTypeFilterName(productCode);
    const documentTypeCodes = viewModelService.productMetadata.get('pc').types.getTypelist('typekey.DocumentType').codes;
    const formattedDocumentTypeCodes = documentTypeCodes?.map(code => ({
        code: code.code,
        name: translator({ id: code.name })
    }));
    const { twiaPhoneNumber, tfpaPhoneNumber } = config.phoneNumberConfig;

    const BUILDING_OPTION_NA = {
        key: 0,
        value: 'N/A'
    };

    const isTWIACompany = () => {
        return (jobNumber !== undefined && uwCompany === '2001_00001') || (policyNumber !== undefined && policyNumber.startsWith("TW"));
    };

    const loadDocuments = () => {
        setLoading(true);
        if (policyNumber !== undefined && policyNumber.startsWith("T")) {
            PolicyService.getDocumentsForPolicy(policyNumber, authHeader)
                .then(docs => {
                    setDocuments(docs);
                    setDisplayDocuments(docs);
                    setDocumentCount(docs?.length)
                })
                .catch(e => {
                    showAlert({
                        title: commonMessages.errorUploadTitle,
                        message: messages.failedToRetrievedocuments,
                        status: 'error',
                        icon: 'mi-error-outline',
                        confirmButtonText: commonMessages.ok,
                    }).catch(_.noop);
                })
                .finally(() => {
                    setLoading(false);
                });
        } else {
            SubmissionService.getDocumentsForSubmission(jobNumber, authHeader)
                .then(docs => {
                    setDocuments(docs);
                    setDisplayDocuments(docs);
                    setDocumentCount(docs?.length)
                })
                .catch(e => {
                    showAlert({
                        title: commonMessages.errorUploadTitle,
                        message: messages.unableToDownload,
                        status: 'error',
                        icon: 'mi-error-outline',
                        confirmButtonText: commonMessages.ok,
                    }).catch(_.noop);
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    };

    const getBuildingsFromLocations = (locations) => {
        return _(locations)
            .map(loc => {

                // const address = `${loc.address.addressLine1}, ${loc.address.city}, ${loc.address.state}, ${loc.address.postalCode}`;
                const address = loc.displayName;

                return loc.buildings.map(building => ({
                    fixedID: building.fixedID,
                    publicID: building.publicID,
                    buildingID: building.buildingNumber,
                    description: building.description,
                    location: loc,
                    locationNum_TWIA: loc.locationNum_TWIA,
                    address: address
                }));
            })
            .flatten()
            .sortBy('buildingID') // Assumption is that public id grows
            .sortBy((location) => {
                return location.locationNum_TWIA;
            })
            .valueOf();
    }

    const getBuildingOptions = (buildings) => {
        const options = [];

        options.push(BUILDING_OPTION_NA);

        if (!_.isNil(buildings)) {
            buildings.forEach((building) => {
                const option = `${building.buildingID}: ${_.isNil(building.description) ? '' : building.description} (${building.address})`;

                options.push({ key: building.fixedID, value: option });
            });
        }

        return options;
    }

    const fetchLocationandBuildingData = async () => {

        if (jobNumber !== undefined) {
            await CpCoverablesService.getLocationsAndBuildingsForJob_TWIA(
                jobNumber,
                authHeader
            )
                .then(locations => {
                    let buildingList;

                    if (!_.isNil(locations)) {
                        buildingList = getBuildingsFromLocations(locations);
                    }

                    const buildingOptions = getBuildingOptions(buildingList);
                    const mappedBuildings = buildingOptions && buildingOptions.map(building => {
                        return {
                            code: building.key,
                            name: building.value
                        };
                    });

                    setBuildings(mappedBuildings);
                })
                .catch(retrieveError => {
                    showAlert({
                        title: messages.failedToRetrievedocuments,
                        message: messages.failedToRetrievedocuments,
                        status: 'error',
                        icon: 'mi-error-outline',
                        confirmButtonText: commonMessages.ok,
                    }).catch(_.noop);
                })
                .finally(() => {
                    setLoading(false);
                });
        } else {

            const options = [];

            options.push(BUILDING_OPTION_NA);

            const mappedBuilding = options.map(building => {
                return {
                    code: building.key,
                    name: building.value
                };
            });

            setBuildings(mappedBuilding);
        }
    }

    useEffect(() => {

        if (productCode !== undefined && productCode === 'CommercialProperty') {
            fetchLocationandBuildingData();
        }

        if (isTWIACompany()) {
            const allowedDocumentTypes = viewModelService.productMetadata.get('pc').types.getTypelist('typekey.DocumentType').getFilter(documentFilterType).codes;
            const mappedDocTypeValues = allowedDocumentTypes && allowedDocumentTypes.map(docType => {
                return {
                    code: docType.code,
                    name: translator({
                        id: docType.name,
                        defaultMessage: docType.name,
                    }),
                };
            });

            setDocumentTypes(mappedDocTypeValues);
        } else {
            const allowedDocumentTypesTFPA = viewModelService.productMetadata.get('pc').types.getTypelist('typekey.DocumentType');
            const docTypeValues = ['cancellation_doc_ext', 'decline_letter', 'valuation_worksheet_ext', 'underwriting_response_ext', 'policy_change_ext', 'appeal_ext', 'other'].map((key) => {
                return allowedDocumentTypesTFPA.getCode(key);
            });

            const mappedDocTypeValuesTFPA = docTypeValues && docTypeValues.map(docType => {
                return {
                    code: docType.code,
                    name: translator({
                        id: docType.name,
                        defaultMessage: docType.name,
                    }),
                };
            });

            setDocumentTypes(mappedDocTypeValuesTFPA);
        }

        setDocumentSearch('');
        loadDocuments();
    }, []);

    const onUploadDocument = useCallback(item => {
        const documentMetaDataTemplate = {
            docUID: '001',
            documentType: documentType,
            securityType: 'unrestricted',
            status: 'approved',
            jobNumber,
            policyNumber,
            name: item.name,
            sessionID: '-'
        };

        setLoading(true);
        ENTGatewayDocumentService.uploadDocument(
            item,
            documentMetaDataTemplate,
            authHeader
        )
            .then(loadDocuments)
            .catch(() => {
                showAlert({
                    title: commonMessages.errorUploadTitle,
                    message: isTWIACompany() ? translator(messages.uploadFailedMessage, {
                        uwCompanyName: "TWIA",
                        contactNumber: twiaPhoneNumber,
                    })
                        : translator(messages.uploadFailedMessage, {
                            uwCompanyName: "TFPA",
                            contactNumber: tfpaPhoneNumber,
                        }),
                    status: 'error',
                    icon: 'mi-error-outline',
                    confirmButtonText: commonMessages.ok,
                }).catch(_.noop);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [loadDocuments, jobNumber, policyNumber, showAlert, authHeader, documentType, isTWIACompany, twiaPhoneNumber, tfpaPhoneNumber, translator]);

    const onChangeDocumentType = useCallback(
        docType => {
            setDocumentType(docType);
        },
        [setDocumentType]
    );

    const onChangeDocumentSearch = useCallback(
        searchTerm => {
            if (searchTerm && documents && documents.length > 0) {
                const filtered = documents?.filter(doc => doc.name?.toLowerCase().includes(searchTerm?.toLowerCase()));

                setDisplayDocuments(filtered);
            } else {
                setDisplayDocuments(documents);
            }

            setDocumentSearch(searchTerm);
        },
        [setDocumentSearch, setDisplayDocuments, documents]
    );

    const onDocumentUpdate = useCallback(item => {
        if (isTWIACompany() && (productCode !== undefined && productCode === 'CommercialProperty')
            && _.isNil(item.buildingFixedID_TWIA)) {

            showAlert({
                title: commonMessages.errorUploadTitle,
                message: messages.buildingRequired,
                status: 'error',
                icon: 'mi-error-outline',
                confirmButtonText: commonMessages.close,
            })

            return false;
        }
        
        setLoading(true);
        ENTGatewayDocumentService.updateDocument(
            item,
            authHeader
        )
            .then(result => {
                item.buildingDisplay_TWIA = _.get(result, 'buildingDisplay_TWIA');
                item.edited = false;
            })
            .catch(() => {
                showAlert({
                    title: commonMessages.errorUploadTitle,
                    message: isTWIACompany() ? translator(messages.uploadFailedMessage, {
                        uwCompanyName: "TWIA",
                        contactNumber: twiaPhoneNumber,
                    })
                        : translator(messages.uploadFailedMessage, {
                            uwCompanyName: "TFPA",
                            contactNumber: tfpaPhoneNumber,
                        }),
                    status: 'error',
                    icon: 'mi-error-outline',
                    confirmButtonText: commonMessages.ok,
                }).catch(_.noop);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [authHeader, showAlert, isTWIACompany, translator, twiaPhoneNumber, tfpaPhoneNumber]);

    const isEditable = (item) => {
        return _.isNil(item.description) || item.edited;
    };

    const onDownloadDocument = useCallback(async item => {
        setLoading(true);

        if(policyNumber || offerNumber) {
            _.set(item, 'policyNumber', policyNumber || offerNumber);
        }

        try {
            const sessionID = await ENTGatewayDocumentService.generateDownloadToken(item, authHeader);
            const downloadURL = ENTGatewayDocumentService.downloadDocument(item.docUID, sessionID);

            window.location.href = `${downloadURL}&job=${jobNumber || policyNumber}`;
        } catch (error) {
            showAlert({
                title: commonMessages.errorUploadTitle,
                message: commonMessages.errorGenerateUploadToken,
                status: 'error',
                icon: 'mi-error-outline',
                confirmButtonText: commonMessages.ok,
            })
                .catch(_.noop);
        } finally {
            setLoading(false);
        }
    }, [authHeader, jobNumber, policyNumber, showAlert]);

    const getDocumentName = useCallback((item, index) => (
        <Link style={{ justifyItems: 'left' }} onClick={() => { onDownloadDocument(item) }}>{item.name}</Link>
    ), [onDownloadDocument]);

    const getDocumentType = (item) => {
        const code = formattedDocumentTypeCodes?.find(val => val.code == item.documentType);

        return code ? code.name : item.documentType;
    };

    const writeDocumentValue = useCallback((index, path, value) => {
        const doc = _.cloneDeep(displayDocuments[index]);
        _.set(doc, path, value);
        doc.edited = true;
        const updatedDocuments = _.cloneDeep(displayDocuments);
        updatedDocuments[index] = doc;
        setDocuments(updatedDocuments);
        setDisplayDocuments(updatedDocuments);
        refreshData();
    }, [displayDocuments, setDocuments, setDisplayDocuments, refreshData]);

    const getDocumentDescription = useCallback((item, index) => {
        return (
            isEditable(item) ?
                <InputField
                    id={`description${index}`}
                    path="description"
                    onValueChange={(value) => { writeDocumentValue(index, 'description', value) }}
                    value={item.description}
                    required
                    showRequired
                /> : item.description
        )
    }, [writeDocumentValue]);

    const getDocumentBuilding = useCallback((item, index) => {
        return (
            isEditable(item) ?
                <DropdownSelectField
                    id={`building${index}`}
                    alwaysShowPlaceholder={false}
                    availableValues={buildings}
                    path="buildingFixedID_TWIA"
                    onValueChange={(value) => { writeDocumentValue(index, 'buildingFixedID_TWIA', value) }}
                    value={item.buildingFixedID_TWIA}
                /> : item.buildingDisplay_TWIA
        )
    }, [writeDocumentValue]);

    const getDocumentUploaded = useCallback((item, index) => (
        <DateField
            id={`uploadedDate${index}`}
            value={item.dateModified}
            readOnly
            displayFormat="vshort"
        />
    ), []);

    const getDocumentUpdated = useCallback((item, index) => (
        isEditable(item) ?
            <Button
                id={`update${index}`}
                onClick={() => onDocumentUpdate(item)}
                size="small"
                icon="mi-save"
            /> : <div></div>
    ), []);



    const overrideProps = {
        '@field': {
            labelPosition: breakpoint === 'desktop' ? 'left' : 'top',
        },
        documentUploadContainer: {
            visible: !loading
        },
        loadingIndicator: {
            visible: loading
        },
        uploadDocumentType: {
            availableValues: documentTypes,
            value: documentType
        },
        uploadDocument: {
            disabled: documentType === undefined
        },
        documentSearch: {
            visible: documents && documents.length > 0,
            value: documentSearch
        },
        documentsTable: {
            data: displayDocuments
        },
        buildingColumn: {
            visible: productCode !== undefined && productCode !== 'Homeowners',
            readOnly: buildings && buildings.length === 1
        },
        uploadInstructionsTWIA: {
            visible: productCode !== undefined && isTWIACompany() && productCode === 'Homeowners',
        },
        uploadInstructions: {
            visible: productCode !== undefined && productCode === 'CommercialProperty',
        },
        uploadInstructionsTFPA: {
            visible: productCode !== undefined && !isTWIACompany() && productCode === 'Homeowners',
        },
    };

    const resolvers = {
        resolveClassNameMap: styles,
        resolveComponentMap: {},
        resolveCallbackMap: {
            onUploadDocument,
            onChangeDocumentType,
            onChangeDocumentSearch,
            getDocumentName,
            getDocumentDescription,
            getDocumentType,
            getDocumentBuilding,
            getDocumentUploaded,
            getDocumentUpdated
        },
    };

    return (
        <ViewModelForm
            uiProps={metadata.pageContent}
            overrideProps={overrideProps}
            callbackMap={resolvers.resolveCallbackMap}
            componentMap={resolvers.resolveComponentMap}
            classNameMap={resolvers.resolveClassNameMap}
        />
    );
}

export default DocumentUploadComponent;
